import { collectionColors, font } from "../CollectionsTab/tokens";
import { DerinUserRole } from "../../../api/derin";

export const ROLE_OPTIONS: { value: DerinUserRole; label: string }[] = [
  { value: "Admin", label: "Admin" },
  { value: "Viewer", label: "Viewer" },
];

/** Native select in the add-user design: white, bordered, 36px. */
export const selectSx = {
  boxSizing: "border-box" as const,
  width: "100%",
  height: "36px",
  padding: "4px 12px",
  background: collectionColors.white,
  border: `1px solid ${collectionColors.border}`,
  borderRadius: "8px",
  outline: "none",
  fontFamily: font,
  fontSize: "14px",
  color: collectionColors.textPrimary,
  cursor: "pointer",
  "&:disabled": { background: collectionColors.surfaceLight, cursor: "default" },
};

/** Bordered pill showing a role: dark for Admin, grey for Viewer. */
export const roleChipSx = (role: DerinUserRole | null | undefined) => ({
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: "8px",
  fontFamily: font,
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "16px",
  whiteSpace: "nowrap" as const,
  ...(role === "Admin"
    ? { background: "#030213", color: "#FFFFFF" }
    : { background: "#ECEEF2", color: "#030213" }),
});

/** Grey pill with an organization's name. */
export const organizationChipSx = {
  display: "inline-flex",
  alignItems: "center",
  padding: "2px 8px",
  borderRadius: "6px",
  background: "#ECEEF2",
  fontFamily: font,
  fontWeight: 500,
  fontSize: "12px",
  lineHeight: "16px",
  color: "#030213",
  whiteSpace: "nowrap" as const,
};

/**
 * An organization's name for a list, with its slug added only when another active
 * organization has the same name — otherwise the two would be indistinguishable.
 */
export const organizationLabel = (
  organizationId: string,
  name: string,
  organizations: { id: string; name: string; orgId: string }[]
): string => {
  const key = name.trim().toLowerCase();
  const sameName = organizations.filter((o) => o.name.trim().toLowerCase() === key);
  if (sameName.length < 2) return name;
  const slug = organizations.find((o) => o.id === organizationId)?.orgId;
  return slug ? `${name} (${slug})` : name;
};

export const initialsOf = (firstName?: string, lastName?: string): string =>
  `${(firstName ?? "").charAt(0)}${(lastName ?? "").charAt(0)}`.toUpperCase() || "?";

export const isValidEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim());

/** Amber notice under a field: "no user found", "email already registered". */
export const warningNoticeSx = {
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid #F3D88A",
  background: "#FEF8E7",
  fontFamily: font,
  fontSize: "13px",
  lineHeight: "18px",
  color: "#6B4E00",
};

/** Red notice inside a destructive confirmation. */
export const dangerNoticeSx = {
  padding: "10px 12px",
  borderRadius: "8px",
  border: "1px solid rgba(220, 38, 38, 0.25)",
  background: collectionColors.dangerSurface,
  fontFamily: font,
  fontSize: "13px",
  lineHeight: "18px",
  color: "#991B1B",
};

/** Red border on a field whose value is the problem the notice below explains. */
export const invalidFieldSx = { boxShadow: `0 0 0 1px ${collectionColors.danger}` };

/** "Jan 2024". */
export const formatMemberSince = (value: string | undefined): string | null =>
  value ? new Date(value).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : null;
